import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { PlatformRole } from '@leanin/shared/views/profile';
import { sessionQuery, type MyContext } from '@/lib/queries/identity';
import { groupKeys } from '@/lib/queries/groups';

type RouteContext = { readonly queryClient: QueryClient };

type AuthedContext = NonNullable<MyContext>;

/**
 * Returned `Response` from `redirect()`. Callers throw whatever the thunk
 * builds; passing a thunk (rather than a serialised object) lets each call
 * site keep TanStack's typed `to`/`params` narrowing.
 */
type BounceThunk = () => Response;

/**
 * Load the viewer's session, bouncing to /auth/login on any failure
 * (fetch error or null member). Exported for routes that need the
 * session itself — e.g. `/profile/me` redirecting to the viewer's
 * `/members/:id` — without a role gate.
 */
export async function requireSession(
  context: RouteContext,
): Promise<AuthedContext> {
  let ctx: MyContext;
  try {
    ctx = await context.queryClient.ensureQueryData(sessionQuery);
  } catch {
    throw redirect({ to: '/auth/login' });
  }
  if (ctx === null) throw redirect({ to: '/auth/login' });
  return ctx;
}

/**
 * Bounce viewers whose `platformRole` doesn't match. Returns the loaded
 * session so the caller can read viewer fields without a second fetch.
 */
export async function requirePlatformRole(
  context: RouteContext,
  opts: { readonly role: PlatformRole; readonly bounceTo: BounceThunk },
): Promise<AuthedContext> {
  const ctx = await requireSession(context);
  if (ctx.member.platformRole !== opts.role) throw opts.bounceTo();
  return ctx;
}

/**
 * Group admin gate: passes platform admins through unconditionally, otherwise
 * fetches the viewer's role for `groupId` and bounces unless they're a leader.
 */
export async function requireLeaderOrAdmin(
  context: RouteContext,
  opts: { readonly groupId: string; readonly bounceTo: BounceThunk },
): Promise<AuthedContext> {
  const ctx = await requireSession(context);
  if (ctx.session.platformRole === 'admin') return ctx;
  const role = await context.queryClient.ensureQueryData(
    groupKeys.viewerRole(opts.groupId),
  );
  if (role !== 'leader') throw opts.bounceTo();
  return ctx;
}
